import Constant from '@/common/constant/Constant';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { removePanelInitData } from '@/component/multiPanel/panel/handler/PanelHandler';

import {
    createNewTicketAPI, saveDraftTicketAPI,
} from '@/service/ticketService';
import Utils from '@/common/utils/utils';
import { validateErrorTicket } from '@/component/common/ValidateTicket';
import { sortListByOrder } from './TicketHandler';

import { copyFileOfTicketDraftTo, handleUploadFilesForTicket } from '@/component/common/layout/addTicket/handler/AddTicketHandler';
import { getUserById } from '@/service/userService';
import { getCorporationById } from '@/service/organizationService';
import { 
    getProjectById,
    getAllProjectListInCorporation, 
    getListGuestProject, 
    getActiveB2CCustomerListInProjectByProjectId } from '@/service/projectService';
import { getAllUserInBaseProject } from '@/service/userService';
import { I18n } from 'aws-amplify';
import LogKibanaConstant from '@/common/constant/LogKibanaConstant';
import { createKibanaDataContentOperation } from '@/component/system/logKibanaHandler/LogKibanaHandler';
import { createKibanaLogAPI } from '@/service/logKibanaService';
import { updateUserWorkLastForm } from '@/common/utils/ServiceFactoryUtil';

/**
 * Update noticeFormList by projectInuse
 * @param {*} argsCommon
 */
export const setNoticeFormListByProjectInuse = (argsCommon) => {
    if (Utils.isEmptyArray(argsCommon.formList)) {
        argsCommon.setNoticeFormList([]);
        return;
    }
    let noticeFormList = [];
    argsCommon.formList.forEach((form) => {
        // check type of form is notice and includes projectInuse
        let isNoticeForm =
            form.type === Constant.FORM_TYPE.FORM_REQ_NOTICE &&
            form.usingProjects.includes(argsCommon.projectInuseDetail.id);
        if (isNoticeForm) {
            noticeFormList.push(form);
        }
    });
    noticeFormList = sortListByOrder(noticeFormList, argsCommon.projectInuseDetail.id);
    argsCommon.setNoticeFormList(noticeFormList);
};

/**
 * Create notice ticket handler
 * @param {*} formData
 * @param {*} projectTargetList
 * @param {*} userDetail
 * @param {*} corporationDetail
 * @param {*} panelId
 * @param {*} argsAll
 * @param {*} fieldListData
 * @returns noticeTicket
 */
export const createNoticeTicketHandler = async (
    formData,
    userDetail,
    panelId,
    panelType,
    tabId,
    corporationDetail,
    selectedForm,
    fieldListData,
    argsAll,
) => {
    // check selectedForm
    if (Utils.isEmptyString(selectedForm)) {
        argsAll.setIsErrorSelected(true);
        
        return;
    }

    const noticeCustomer = formData.noticeCustomer ?? argsAll.noticeCustomer;

    let draftTicketInfo = {
        isSendDraft: argsAll?.isDraft ? true : false,
        fileUploadedList: copyFileOfTicketDraftTo(argsAll.fileUpdatedList),
        oldDraftTicketId: argsAll?.detailTicket?.id ? argsAll?.detailTicket?.id : ''
    };

    // Validate when project type is deskOnPublicOff
    if (Utils.isPrivateDesk(argsAll.projectInuseDetail) && draftTicketInfo.isSendDraft) {
        let isCanNotSendDraft = false;
        switch (noticeCustomer) {
            case Constant.NOTICE_ALL_CUSTOMER.ALL_GUEST_CUSTOMER_USER:
            case Constant.NOTICE_ALL_CUSTOMER.ALL_CUSTOMER_PROJECT:
                isCanNotSendDraft = true;
                break;
            default:
                const customerDetail = argsAll?.noticeCustomerList?.find(cus =>
                    cus.id === noticeCustomer
                );
                if (
                    !customerDetail ||
                    (customerDetail && [
                        Constant.NOTICE_RECIPIENT_OPTIONS.ALL_CUSTOMER_PROJECTS,
                        Constant.NOTICE_RECIPIENT_OPTIONS.ALL_GUEST_CUSTOMER_USERS
                    ].includes(customerDetail.groupLabel))
                ) {
                    isCanNotSendDraft = true;
                }
                break;
        }

        if (isCanNotSendDraft) {
            argsAll?.setIsCanNotSendTicketWhenDeskOnPublicOff(true);
            argsAll?.setIsLoading(false);
            return;
        }
    }

    // Validate selected customer before sent ticket notice
    if (!validateSelectedCustomerBeforeSentTicketNotice(argsAll)) {
        argsAll.setErrorCreateTicket(
            'noticeCustomer',
            { type: 'manual', message: I18n.get('COMM01MSG11') },
            { shouldFocus: false }
        );
        argsAll.setIsLoading(false);
        return;
    }

    // check projectTargetLis

    argsAll.setIsLoading(true);

    // convert field to request body
    let fields = await Utils.getCustomFieldValueInFormData(formData, fieldListData);
    let statusField = fieldListData.filter(
        (field) => field.structure.UI === Constant.FIELD_UI.WORKFLOW
    );
    let status = '';
    if (!Utils.isEmptyArray(statusField)) {
        status = statusField[0].fieldId;
    }

    // validate
    if (validateErrorTicket(argsAll, fields)) {
        argsAll.setIsLoading(false);
        return;
    }

    let uploadFileS3Result = {
        fileListSuccess: [],
        suffixOfAttachments: {},
        listFileError: []
    };

    uploadFileS3Result = await handleUploadFilesForTicket(
        formData,
        argsAll.setFileUploading,
        argsAll.fileUploadList
    );

    let requestBody = {
        createTicketRequestDto: {
            corporationOrigin: corporationDetail.id,
            createUserId: userDetail.id,
            projectOrigin: argsAll.projectInuseDetail.id,
            corporationTarget: corporationDetail.id,
            ticketType: Constant.TICKET_TYPE.NOTICE,
            projectTargetObjList: formData.projectTargetObjList,
            affiliationOrganization: corporationDetail.id,
            affiliationProject: argsAll.projectInuseDetail.id,
            customer: noticeCustomer,
            status: status,
            fields: fields,
            type: argsAll.selectedForm,
            liveOrArchived: Constant.LIVE_OR_ARCHIVED.LIVE,
            public: Constant.TICKET_PUBLIC.PUBLIC,
            fileListSuccess: uploadFileS3Result.fileListSuccess,
            suffixOfAttachments: JSON.stringify(uploadFileS3Result?.suffixOfAttachments),
            ...draftTicketInfo
        },
    };

    //call API create ticket
    let newTicket = await createNewTicketAPI(argsAll.API, requestBody);
    if (!newTicket?.id) {
        if (!newTicket.createTicketStatus && newTicket?.errorMessage === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
            // Reload
            argsAll.setIsOpenModalReloadPage(true);
        }
        
        argsAll.setIsLoading(false);
        return;
    }
    
    let dataUpdateLastForm = {
        typeLastTicket: Constant.TYPE_LAST_FORM.LAST_FORM_NOTICE,
        formId: argsAll.selectedForm,
        projectTargetId: formData?.projectTarget,
        corpTargetId: formData?.corporationTarget ? formData?.corporationTarget : argsAll?.corporationDetail?.id,
        method: formData.noticeCustomer
    }

    await updateUserWorkLastForm(dataUpdateLastForm, argsAll.setUserDetail, argsAll.userDetail);

    argsAll.setIsLoading(false);
    if (!tabId) {
        tabId = argsAll.ticketId;
    }
    let lastUserAction = LogKibanaConstant.CONTENT_OPERATION_ACTION.ADD;
    let usingFormName = Utils.convertJSONDataToObject(argsAll.selectedFormInfo.name)?.jp?.text;
    let dataLog = {
        rootCorporationDetail: {
            id: newTicket.affiliationOrganization
        },
        rootProjectDetail: {
            id: newTicket.affiliationProject
        },
        corporationDetail: argsAll.corporationDetail,
        projectDetail: argsAll.projectInuseDetail,
        userDetail: argsAll.userDetail,
        ticketDetail: {
            id: newTicket.id,
            outline: newTicket.outline,
            creator: newTicket.creator,
            usingFormName: usingFormName,
            createdAt: newTicket.createdAt
        },
        metaData: {
            formDetailId: argsAll.selectedForm,
            ticketType: Constant.TICKET_TYPE.NOTICE
        },
        ticketCreationScreen: LogKibanaConstant.TICKET_CREATION_SCREEN.SOLUTIONDESK
    }

    let logKibanaData = createKibanaDataContentOperation(
        lastUserAction,
        dataLog
    );
    let param = {
        logType: LogKibanaConstant.CONTENT_OPERATION_ACTION.ADD,
        logKibanaData: logKibanaData
    };
    createKibanaLogAPI(argsAll.API, param);
    argsAll.setLastUserAction(null);
    removePanelInitData(panelId, tabId, argsAll.panelInitData, argsAll.setPanelInitData);

    argsAll.updatePanelAfterCreateTicket({
        panelId: panelId,
        tabId: tabId,
        panelType: panelType,
        ticketId: newTicket.id,
        ticketOutline: newTicket.outline,
        API: argsAll.API,
    });

    argsAll.enqueueSnackbar(I18n.get('MSG-CM-SEND-TICKET-SUCCESS'), {
        variant: 'success',
        autoHideDuration: 3000
    });
    return newTicket;
};


/**
 * validateSelectedCustomerBeforeSentTicketNotice
 * @param {*} argsAll 
 * @returns 
 */
export const validateSelectedCustomerBeforeSentTicketNotice = (argsAll) => {
    if (!argsAll.noticeCustomer) return false;

    let statusValidate = false;
    switch (argsAll.noticeCustomer) {
        case Constant.NOTICE_ALL_CUSTOMER.ALL_CUSTOMER_PROJECT:
            statusValidate = !Utils.isEmptyArray(argsAll.listExternalCustomerProject);
            break;
        case Constant.NOTICE_ALL_CUSTOMER.ALL_GUEST_CUSTOMER_USER:
            statusValidate = !Utils.isEmptyArray(argsAll.listCustomerUser);
            break;
        case Constant.NOTICE_ALL_CUSTOMER.ALL_GENERAL_PROJECT:
            statusValidate = !Utils.isEmptyArray(argsAll.generalProjectList);
            break;
        case Constant.NOTICE_ALL_CUSTOMER.ALL_USER_IN_BASE_PROJECT:
            statusValidate = !Utils.isEmptyArray(argsAll.baseProjectUserList);
            break;
        default:
            let noticeTargetData = argsAll.noticeCustomerList.find(noticeTarget => noticeTarget.id === argsAll.noticeCustomer);
            statusValidate = !Utils.isEmptyObject(noticeTargetData);
            break;
    }

    return statusValidate;
}


export const draftNoticeTicketHandler = async (
    formData,
    userDetail,
    corporationDetail,
    selectedForm,
    fieldListData,
    argsAll,
) => {
    // check selectedForm
    if (Utils.isEmptyString(selectedForm)) {
        argsAll.setIsErrorSelected(true);
        
        return;
    }

    // Validate selected customer before sent ticket notice
    if (!validateSelectedCustomerBeforeSentTicketNotice(argsAll)) {
        argsAll.setErrorCreateTicket(
            'noticeCustomer',
            { type: 'manual', message: I18n.get('COMM01MSG11') },
            { shouldFocus: false }
        );
        argsAll.setIsLoading(false);
        return;
    }

    argsAll.setIsLoading(true);

    // convert field to request body
    let fields = await Utils.getCustomFieldValueInFormData(formData, fieldListData);
    let statusField = fieldListData.filter(
        (field) => field.structure.UI === Constant.FIELD_UI.WORKFLOW
    );
    let status = '';
    if (!Utils.isEmptyArray(statusField)) {
        status = statusField[0].fieldId;
    }

    // validate
    if (validateErrorTicket(argsAll, fields)) {
        argsAll.setIsLoading(false);
        return;
    }

    let uploadFileS3Result = {
        fileListSuccess: [],
        suffixOfAttachments: {},
        listFileError: []
    };
    uploadFileS3Result = await handleUploadFilesForTicket(
        formData,
        argsAll.setFileUploading,
        argsAll.fileUploadList
    );

    let draftTicketInfo = {
        isSendDraft: argsAll?.isDraft ? true : false,
        fileUploadedList: copyFileOfTicketDraftTo(argsAll.fileUpdatedList),
        oldDraftTicketId: argsAll?.detailTicket?.id ? argsAll?.detailTicket?.id : ''
    };

    let requestBody = {
        createTicketRequestDto: {
            corporationOrigin: corporationDetail.id,
            createUserId: userDetail.id,
            projectOrigin: localStorageUtil.getSelectedProjectId(),
            corporationTarget: corporationDetail.id,
            ticketType: Constant.TICKET_TYPE.NOTICE,
            projectTargetObjList: formData.projectTargetObjList,
            affiliationOrganization: corporationDetail.id,
            affiliationProject: localStorageUtil.getSelectedProjectId(),
            customer: formData.noticeCustomer,
            status: status,
            fields: fields,
            type: argsAll.selectedForm,
            liveOrArchived: Constant.LIVE_OR_ARCHIVED.LIVE,
            public: Constant.TICKET_PUBLIC.DRAFT,
            fileListSuccess: uploadFileS3Result.fileListSuccess,
            suffixOfAttachments: JSON.stringify(uploadFileS3Result?.suffixOfAttachments),
            ...draftTicketInfo
        },
    };



    //call API create ticket
    let newTicket = await saveDraftTicketAPI(argsAll.API, requestBody);
    if (!newTicket?.id) {
        if (!newTicket.createTicketStatus && newTicket?.errorMessage === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
            // Reload
            argsAll.setIsLoading(false);
            argsAll.setIsOpenModalReloadPage(true);
        }
        return;
    }

    let dataUpdateLastForm = {
        typeLastTicket: Constant.TYPE_LAST_FORM.LAST_FORM_NOTICE,
        formId: argsAll?.selectedForm,
        projectTargetId: formData?.projectTarget,
        corpTargetId: formData?.corporationTarget ? formData?.corporationTarget : argsAll?.corporationDetail?.id,
        method: formData.noticeCustomer,
    }

    await updateUserWorkLastForm(dataUpdateLastForm, argsAll.setUserDetail, argsAll.userDetail);
    argsAll.setIsLoading(false);
    return newTicket;
};

export const sortListProjectByDictionaryASC = (list) => {
    return list.sort((a, b) => {
        let nameA = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, a.name);
        let nameB = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, b.name);
        return nameA.localeCompare(nameB);
    });
}

export const sortExternalCustomerProjectByDictionaryASC = (list) => {
    return list.sort((a, b) => {
        let nameA = a.label;
        let nameB = b.label;
        return nameA.localeCompare(nameB);
    });
}

export const sortListByDictionaryASC = list => {
    if (Utils.isEmptyArray(list)) {
        return [];
    }
    return list.sort((a, b) => {
        let nameA = a.fullName ? a.fullName : Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, a);
        let nameB = b.fullName ? b.fullName : Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, b);
        return nameA.localeCompare(nameB);
    });
}

export const sortListProjectByDictionaryDESC = (list) => {
    return list.sort((b, a) => {
        let nameA = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, a.name);
        let nameB = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, b.name);
        return nameA.localeCompare(nameB);
    });
}

export const getAllCustomerUsers = async (API, projectDetail) => {
    let allCustomerObject = {
        allCustomerUser: [],
    };

    const customerUsers = await getActiveB2CCustomerListInProjectByProjectId(API, projectDetail.id);
    //get customer user and user in base project
    let allCustomerUser = [];
    if (!Utils.isEmptyArray(customerUsers)) {
        customerUsers?.forEach(user => {
            let userClone = { ...user };
            userClone.label = Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, user);
            if (user.type === Constant.USER_TYPE.CUSTOMER && user.active === Constant.ACTIVE_FLAG.ACTIVE) {
                userClone.groupLabel = 'ゲストカスタマーユーザー';
                allCustomerUser.push(userClone);
            }
        });
    }

    allCustomerObject.allCustomerUser = allCustomerUser;
    return allCustomerObject;
}

export const getAllCustomerToNotice = async (API, corporationDetail, projectDetail, userDetail, props) => {
    let allCustomerObject = {
        allGeneralProject: [],
        allCustomerUser: [],
        allUserInBaseProject: [],
        allExternalCustomerProject: [],
        projectBase: [],
        guestCustomerProject: []
    };
    
    let isDeskOnPublicOn = Utils.isPublicDesk(projectDetail);
    let isDeskOnPublicOf = Utils.isPrivateDesk(projectDetail);
    let draftNoticeCustomer = props?.detailTicket?.customer;
    let draftNoticeCustomerPrefix = Utils.getPrefixOfId(draftNoticeCustomer);
    
    const resGetAllCustomerToNotice = await Promise.all([
        // query 1: all prj in org
        getAllProjectListInCorporation(API, corporationDetail.id, Constant.ACTIVE_FLAG.ACTIVE),

        // query 2: all b2c in prj
        (isDeskOnPublicOn || (isDeskOnPublicOf && draftNoticeCustomer === Constant.NOTICE_GROUP_CUSTOMER.ALL_GUEST_CUSTOMER_USER))
            ? getActiveB2CCustomerListInProjectByProjectId(API, projectDetail.id)
            : draftNoticeCustomerPrefix === Constant.ID_PREFIX.USER
                ? [{ ...await getUserById(null, draftNoticeCustomer) }]
                : [],

        // query 3: all user in basePrj
        getAllUserInBaseProject(API, corporationDetail.id),
        
        // query 4: all b2b
        (isDeskOnPublicOn || (isDeskOnPublicOf && draftNoticeCustomer === Constant.NOTICE_GROUP_CUSTOMER.ALL_CUSTOMER_PROJECT))
            ? getListGuestProject(API, projectDetail)
            : draftNoticeCustomerPrefix === Constant.ID_PREFIX.PROJECT
                ? { listProjectDetail: [{ ...await getNoticeCustomerProject(draftNoticeCustomer)}] }
                : {}
    ]);
    
    // get general project
    let allGeneralProject = [];
    if (!Utils.isEmptyArray(resGetAllCustomerToNotice[0])) {
        // add inusePrj because inusePrj in allGeneralProject
        resGetAllCustomerToNotice[0]?.forEach(project => {

            if(project.type === Constant.PROJECT_TYPE.BASE){
                allCustomerObject.projectBase = [project]
                return;
            }

            if(project.type === Constant.PROJECT_TYPE.CUSTOMER){
                allCustomerObject.guestCustomerProject = [project]
                return;
            }
            let projectClone = { ...project };
            projectClone.groupLabel = '組織内プロジェクト';
            projectClone.label = `${Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, project.name)} (${Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, corporationDetail.name)})`;
            allGeneralProject.push(projectClone);
        });
    }
    //get customer user and user in base project
    let allCustomerUser = [];
    if (!Utils.isEmptyArray(resGetAllCustomerToNotice[1])) {
        resGetAllCustomerToNotice[1]?.forEach(user => {
            let userClone = { ...user };
            userClone.label = Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, user);
            if (user.active === Constant.ACTIVE_FLAG.ACTIVE) {
                userClone.groupLabel = 'ゲストカスタマーユーザー'
                allCustomerUser.push(userClone)
            }
        });
    }


    let allUserInBaseProject = [];
    if (!Utils.isEmptyArray(resGetAllCustomerToNotice[2])) {

        resGetAllCustomerToNotice[2]?.forEach(user => {

            let userClone = { ...user };
            
            userClone.label = Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, user);
            if (userDetail.id !== user.id) {
                
                userClone.groupLabel = '組織内ユーザー';
                allUserInBaseProject.push(userClone);
            }
        });
    }
    

    // get all general external customer project (current project is external desk)
    let allExternalCustomerProject = [];
    if (!Utils.isEmptyObject(resGetAllCustomerToNotice[3])) {
        let listProjectDetail = resGetAllCustomerToNotice[3]?.listProjectDetail;
        if (!Utils.isEmptyArray(listProjectDetail)) {
            listProjectDetail?.forEach(project => {
                let projectClone = { ...project };
                projectClone.groupLabel = 'カスタマープロジェクト';
                allExternalCustomerProject.push(projectClone);
            });
        }
    }
    

    
    ;

    allCustomerObject.allGeneralProject = sortListProjectByDictionaryASC(allGeneralProject);
    allCustomerObject.allCustomerUser = sortListByDictionaryASC(allCustomerUser);
    allCustomerObject.allUserInBaseProject = sortListByDictionaryASC(allUserInBaseProject);
    allCustomerObject.allExternalCustomerProject = sortExternalCustomerProjectByDictionaryASC(
        allExternalCustomerProject
    );

    return allCustomerObject;
};

const getNoticeCustomerProject = async (projectId) => {
    if (!projectId) return {};
    let noticeCustomerPrj = {};
    const projectDetail = await getProjectById(null, projectId);
    if (projectDetail) {
        const corporationDetail = await getCorporationById(null, projectDetail.affiliationOrganization)
        noticeCustomerPrj = {
            ...projectDetail,
            label: `${Utils.getTextByLanguage( Constant.LANGUAGE_TYPE.JP, corporationDetail.name)} (${Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, projectDetail.name)})`,
            corporationData: corporationDetail
        }
    }

    return noticeCustomerPrj;
}